import {MapNode} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {CLStore} from 'common/src/redux/store';
import {DateTime} from 'luxon';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import {cardToText} from '../../cardDetailsV2/textualConverters';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const ID_DISTINCTION_NOTE = `\n IMPORTANT: Strictly distinguish ID types. When calling tools, use the prefixed string format: <prefix><object_number> as required by each tool. Never expose internal prefixes to the user — use the object type name and the numeric part in natural language instead.`;

export type GetOrCreateCard = (cardId: number, store: CLStore) => Promise<MapNode>;

export type PreProcessStandaloneAgentResult =
  | {ok: true; promptToUse: string; normalizedCardId: number; card: MapNode}
  | {ok: false; error: string};

export default async function preProcessStandaloneAgent<TScratchpad>(
  params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, TScratchpad>,
  store: CLStore,
  getOrCreateCard: GetOrCreateCard,
): Promise<PreProcessStandaloneAgentResult> {
  const {prompt} = params.input;

  const rawCardId = params.input.cardId ?? null;
  let normalizedCardId = 0;

  if (rawCardId) {
    const parseResult = idHelper.parseCardId(rawCardId);

    if (parseResult.ok === false) {
      return {ok: false, error: formatParseError(parseResult.error)};
    }

    normalizedCardId = parseResult.numericId;
  }

  const card = await getOrCreateCard(normalizedCardId, store);

  const promptWithDateInstructions = prompt.concat(
    `\n When using dates, use the ISO format for the dates. 
      Today's date is ${DateTime.now().toISO()}.`,
  );

  const cardText = cardToText(card, 'full');
  const promptToUse = `${promptWithDateInstructions}\n\nCard state:\n${cardText}${ID_DISTINCTION_NOTE}`;

  return {
    ok: true,
    promptToUse,
    normalizedCardId,
    card,
  };
}
